import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {MessageData} from "types/Message";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import MessageForm from "pages/Message/Form";

function MutateMessage() {
    const {id} = useParams()
    const {data: message, isLoading} = useQuery(
        ['messages', {id}],
        () => ngage.messages.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: MessageData) => ngage.messages.store(data))
    const update = useMutation((data: MessageData) => ngage.messages.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: MessageData, {setSubmitting}: FormikHelpers<MessageData>) {
        // Always ensure the body is set
        const modifiedValues: MessageData = {...values};
        if (!modifiedValues.body) modifiedValues.body = modifiedValues.snippet ?? '';

        // Perform store or update
        id? await handleUpdate(modifiedValues) : await handleStore(modifiedValues);
        await invalidate('messages.all');

        setSubmitting(false);
        navigate('/messages');
    }

    // Handle store mutation
    async function handleStore(values: MessageData) {
        try {
            await store.mutateAsync(values);
            notify("success", "Message Created", `${values.subject} has been created`);
        } catch (error) {
            notify("error", "Message Not Created", `${values.subject} could not be stored`);
            throw error;
        }
    }

    // Handle update mutation
    async function handleUpdate(values: MessageData) {
        try {
            await update.mutateAsync(values);
            notify("success", "Message Updated", `${values.subject} has been updated`);
            await invalidate(['messages', {id}]);
        } catch (error) {
            notify("error", "Message Not Updated", `${values.subject} could not be updated`);
            throw error;
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Engagement': '/', 'Messages': '/messages', 'New Message': '/messages/new'}
    const editBreadcrumbs = {'Engagement': '/', 'Messages': '/messages', 'Edit Message': `/messages/${id}/edit`}

    return (
        <Page title={id ? "Edit Message" : "New Message"}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <MessageForm message={message} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateMessage;